/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import React, { Children, cloneElement } from 'react';
import { Spinner } from '@zapier/design-system';

type AsyncOverlayProps = {
  children: React.ReactElement;
  /** Fixes scroll position and takes up entire screen */
  isFixed?: boolean;
  /** Is indicator visible */
  isVisible?: boolean;
  /** Used to render a custom Indicator */
  renderAsyncIndicator?: () => React.ReactNode;
};

const overlayCss = ({ isFixed }: AsyncOverlayProps) =>
  css({
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'center',
    left: 0,
    position: isFixed ? 'fixed' : 'absolute',
    top: 0,
    width: '100%',
    // Used to render on top of dropdown menus
    zIndex: 901,
  });

/**
 * Renders a loading indicator on top of children content
 */
export const AsyncOverlay = (props: AsyncOverlayProps) => {
  const {
    children,
    isVisible = false,
    renderAsyncIndicator = () => <Spinner />,
  } = props;
  const childrenProps = children?.props || {};
  const childrenStyle = childrenProps?.style || {};

  // Clone the child and set it's position to relative if needed.
  // This allows the overlay to seamlessly cover the whole
  // area of the child.
  return cloneElement(
    children,
    {
      ...childrenProps,
      style: {
        ...childrenStyle,
        position: childrenStyle?.position?.relative,
      },
    },
    [
      ...Children.toArray(children?.props?.children),
      isVisible && (
        <div css={overlayCss(props)} key="indicator">
          {renderAsyncIndicator()}
        </div>
      ),
    ]
  );
};
